import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { UtensilsCrossed, User, Mail, Lock, Building, Phone, MapPin, Plus, X } from 'lucide-react';
import axios from 'axios';

import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { API } from '../contexts/AuthContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'customer',
    tenant_id: ''
  });
  const [addresses, setAddresses] = useState([{ address: '', label: 'Casa', isDefault: true }]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Load tenants for selection (customers can select which restaurant)
    const loadTenants = async () => {
      try {
        const response = await axios.get(`${API}/mobile/tenants/public`);
        setTenants(response.data || []);
        // Si solo hay un tenant, seleccionarlo automáticamente
        if (response.data && response.data.length === 1) {
          setFormData(prev => ({ ...prev, tenant_id: response.data[0].id }));
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
        toast.error('Error al cargar los restaurantes disponibles');
      }
    };
    loadTenants();
  }, []);

  const handleAddAddress = () => {
    setAddresses([...addresses, { address: '', label: '', isDefault: false }]);
  };

  const handleRemoveAddress = (index) => {
    if (addresses.length > 1) {
      const newAddresses = addresses.filter((_, i) => i !== index);
      // Si se eliminó la dirección por defecto, marcar la primera como default
      if (addresses[index].isDefault && newAddresses.length > 0) {
        newAddresses[0].isDefault = true;
      }
      setAddresses(newAddresses);
    }
  };

  const handleAddressChange = (index, field, value) => {
    const newAddresses = [...addresses];
    newAddresses[index][field] = value;
    setAddresses(newAddresses);
  };

  const handleSetDefaultAddress = (index) => {
    const newAddresses = addresses.map((addr, i) => ({
      ...addr,
      isDefault: i === index
    }));
    setAddresses(newAddresses);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validar que haya al menos una dirección con contenido
      const validAddresses = addresses.filter(addr => addr.address.trim() !== '');
      if (validAddresses.length === 0) {
        toast.error('Debes agregar al menos una dirección de entrega');
        setLoading(false);
        return;
      }

      // Validar teléfono
      if (!formData.phone || formData.phone.trim() === '') {
        toast.error('El teléfono es requerido');
        setLoading(false);
        return;
      }

      // Validar tenant seleccionado
      if (!formData.tenant_id || formData.tenant_id.trim() === '') {
        toast.error('Debes seleccionar un restaurante');
        setLoading(false);
        return;
      }

      // Prepare data - remove empty tenant_id
      const dataToSend = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        role: 'customer',
        addresses: validAddresses
      };
      
      // Only add tenant_id if it's not empty
      if (formData.tenant_id && formData.tenant_id.trim() !== '') {
        dataToSend.tenant_id = formData.tenant_id;
      }

      const result = await register(dataToSend);

      if (result.success) {
        toast.success('¡Registro exitoso!');
        navigate('/');
      } else {
        toast.error(result.error || 'Error en el registro');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Error al registrar. Por favor intenta de nuevo.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 px-4 py-8">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMTA3LDUzLDAuMDUpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40"></div>
      
      <div className="w-full max-w-md relative z-10 animate-fade-in">
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-orange-100">
          {/* Logo */}
          <div className="flex flex-col items-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <UtensilsCrossed className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-1">Crear Cuenta</h1>
            <p className="text-sm text-gray-600">Únete a SmartDine</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                Nombre Completo
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="register-name-input"
                  className="pl-10 h-11 border-gray-200 focus:border-orange-400 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                  className="pl-10 h-11 border-gray-200 focus:border-orange-400 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="register-password-input"
                  className="pl-10 h-11 border-gray-200 focus:border-orange-400 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                Teléfono
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1234567890"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="register-phone-input"
                  className="pl-10 h-11 border-gray-200 focus:border-orange-400 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tenant" className="text-sm font-medium text-gray-700">
                Restaurante *
              </Label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                <Select
                  value={formData.tenant_id}
                  onValueChange={(value) => setFormData({ ...formData, tenant_id: value })}
                  required
                >
                  <SelectTrigger className="pl-10 h-11 border-gray-200 focus:border-orange-400 rounded-xl">
                    <SelectValue placeholder="Selecciona un restaurante" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.length === 0 ? (
                      <SelectItem value="loading" disabled>
                        Cargando restaurantes...
                      </SelectItem>
                    ) : (
                      tenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Direcciones de entrega */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-700">
                  Direcciones de Entrega
                </Label>
                <Button
                  type="button"
                  onClick={handleAddAddress}
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 rounded-lg border-orange-200 hover:bg-orange-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>

              {addresses.map((addr, index) => (
                <div key={index} className="space-y-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <Input
                      type="text"
                      placeholder="Etiqueta (Ej: Casa, Trabajo)"
                      value={addr.label}
                      onChange={(e) => handleAddressChange(index, 'label', e.target.value)}
                      className="flex-1 h-9 rounded-lg text-sm"
                    />
                    {addresses.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => handleRemoveAddress(index)}
                        variant="ghost"
                        size="sm"
                        className="ml-2 h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Dirección completa"
                      value={addr.address}
                      onChange={(e) => handleAddressChange(index, 'address', e.target.value)}
                      required
                      className="pl-10 h-10 rounded-lg"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id={`default-${index}`}
                      name="defaultAddress"
                      checked={addr.isDefault}
                      onChange={() => handleSetDefaultAddress(index)}
                      className="w-4 h-4 text-orange-500 focus:ring-orange-400"
                    />
                    <Label htmlFor={`default-${index}`} className="text-xs text-gray-600 cursor-pointer">
                      Dirección predeterminada
                    </Label>
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-500">
                Puedes agregar múltiples direcciones para tus entregas
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full h-11 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl mt-6"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Registrando...
                </div>
              ) : (
                'Registrarse'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿Ya tienes cuenta?{' '}
              <Link
                to="/login"
                data-testid="login-link"
                className="text-orange-600 hover:text-orange-700 font-semibold"
              >
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
